import '@/pages/admin/admin.css';

import { EditarAsesoriaModal } from '@/components/sysadmin/EditarAsesoriaModal';
import { LoadingContainer, SearchableSelect } from '@/components/ui';
import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import { useAsesoriasSysadmin } from '@/hooks';
import { useAsesores, useEmpresasSysadmin } from '@/hooks/useSysadmin';
import type { AsesoriaSysadminDTO, AsesoriasSysadminFilter } from '@/services/sysadmin.service';
import { getAsesoriaEstadoLabel, normalizeAsesoriaEstado, type AsesoriaEstado } from '@/utils/asesoriaStatus';
import { ClipboardList, Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

// ─── Column definitions ───────────────────────────────

type AsesoriaColKey =
  | 'asesoriaId'
  | 'empresaNombre'
  | 'asesorNombre'
  | 'estado'
  | 'fechaInicio'
  | 'fechaFin'
  | 'horas'
  | 'objetivo'
  | 'orden'
  | 'audiosCount'
  | 'evidenciasCount'
  | 'evaluacionesCount';

const ASESORIAS_COLS: ColDef<AsesoriaColKey>[] = [
  { key: 'asesoriaId', label: 'ID', defaultVisible: true },
  { key: 'empresaNombre', label: 'Empresa', defaultVisible: true },
  { key: 'asesorNombre', label: 'Asesor', defaultVisible: true },
  { key: 'estado', label: 'Estado', defaultVisible: true },
  { key: 'fechaInicio', label: 'Inicio', defaultVisible: true },
  { key: 'fechaFin', label: 'Fin', defaultVisible: false },
  { key: 'horas', label: 'Horas', defaultVisible: true },
  { key: 'objetivo', label: 'Objetivo', defaultVisible: false },
  { key: 'orden', label: 'Orden', defaultVisible: false },
  { key: 'audiosCount', label: 'Audios', defaultVisible: false },
  { key: 'evidenciasCount', label: 'Evidencias', defaultVisible: false },
  { key: 'evaluacionesCount', label: 'Evaluaciones', defaultVisible: false },
];

// Column horizontal alignment; unset means left.
const COL_ALIGN: Partial<Record<AsesoriaColKey, 'right' | 'center'>> = {
  asesoriaId: 'right',
  estado: 'center',
  horas: 'right',
  orden: 'right',
  audiosCount: 'right',
  evidenciasCount: 'right',
  evaluacionesCount: 'right',
};

// Estado chip colors mapped to admin design tokens.
const ESTADO_CHIP: Record<AsesoriaEstado, string> = {
  EN_CURSO: 'bg-(--admin-blue-bg) text-(--admin-blue)',
  EN_REVISION: 'bg-(--admin-amber-bg) text-(--admin-amber)',
  FINALIZADA: 'bg-(--admin-green-bg) text-(--admin-green)',
  REQUIERE_CORRECCIONES: 'bg-(--admin-red-bg) text-(--admin-red)',
  REVERTIDA: 'bg-(--admin-surface2) text-(--admin-text2)',
  SIN_EVALUAR: 'bg-(--admin-purple-bg) text-(--admin-purple)',
};

const ESTADOS: AsesoriaEstado[] = ['EN_CURSO', 'EN_REVISION', 'FINALIZADA', 'REQUIERE_CORRECCIONES', 'REVERTIDA', 'SIN_EVALUAR'];

// ─── Helpers ───────────────────────────────────────────

function formatDate(d?: string | null) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function estadoChipClass(estado: string) {
  return ESTADO_CHIP[estado as AsesoriaEstado] ?? 'bg-(--admin-surface2) text-(--admin-text2)';
}

interface EmpresaGroup {
  nombre: string;
  items: AsesoriaSysadminDTO[];
  totalHoras: number;
}

// ─── Main Page ─────────────────────────────────────────

export function GestionAsesoriasPage() {
  const [filtros, setFiltros] = useState<AsesoriasSysadminFilter>({});
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'empresa'>('none');

  const { data: response, isLoading: loadingAsesorias, error, refetch } = useAsesoriasSysadmin(filtros);
  const { empresas, isLoading: loadingEmpresas } = useEmpresasSysadmin();
  const { asesores, isLoading: loadingAsesores } = useAsesores();
  const [editAsesoria, setEditAsesoria] = useState<AsesoriaSysadminDTO | null>(null);

  const {
    visible,
    toggle: toggleCol,
    reset: resetCols,
    activeCount: colCount,
  } = useColumnVisibility<AsesoriaColKey>(ASESORIAS_COLS, 'tourixia:sysadmin-asesorias-cols');

  const asesorias = useMemo(() => response?.asesorias ?? [], [response]);
  const isLoading = loadingAsesorias || loadingEmpresas || loadingAsesores;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return asesorias;
    return asesorias.filter(
      (a) =>
        a.asesoriaId.toString().includes(q) ||
        a.empresaNombre?.toLowerCase().includes(q) ||
        a.asesorNombre?.toLowerCase().includes(q) ||
        a.objetivo?.toLowerCase().includes(q)
    );
  }, [asesorias, search]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => new Date(b.fechaInicio).getTime() - new Date(a.fechaInicio).getTime()),
    [filtered]
  );

  const groups = useMemo(() => {
    if (groupBy !== 'empresa') return null;
    const map = new Map<string, EmpresaGroup>();
    for (const item of sorted) {
      if (!map.has(item.empresaId)) {
        map.set(item.empresaId, { nombre: item.empresaNombre, items: [], totalHoras: 0 });
      }
      const group = map.get(item.empresaId)!;
      group.items.push(item);
      group.totalHoras += item.horas;
    }
    return Array.from(map.entries()).sort((a, b) => a[1].nombre.localeCompare(b[1].nombre));
  }, [sorted, groupBy]);

  const totalHoras = useMemo(() => sorted.reduce((s, a) => s + (a.horas ?? 0), 0), [sorted]);
  const enCurso = useMemo(() => sorted.filter((a) => a.estado === 'EN_CURSO').length, [sorted]);

  const visibleCols = ASESORIAS_COLS.filter((c) => visible[c.key]);
  const hasFilters = Boolean(filtros.empresaId || filtros.asesorId || filtros.estado || search);

  const clearFilters = () => {
    setFiltros({});
    setSearch('');
  };

  if (isLoading && asesorias.length === 0) return <LoadingContainer text="Cargando asesorías..." />;

  if (error) {
    return (
      <div className="admin-card p-4 bg-(--admin-red-bg) border-(--admin-red) text-(--admin-red)">
        <p className="text-[13px] font-medium">Error al cargar asesorías: {error.message}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Metrics */}
      <div className="admin-metrics-row mb-6">
        <div className="admin-metric-card">
          <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">Total Asesorías</div>
          <div className="text-2xl font-bold">{sorted.length}</div>
          <div className="text-[10px] text-(--admin-text3) mt-1">Coinciden con el filtro</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">En Curso</div>
          <div className="text-2xl font-bold text-(--admin-blue)">{enCurso}</div>
          <div className="text-[10px] text-(--admin-text3) mt-1">Con proceso activo</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-widest mb-1">Total Horas</div>
          <div className="text-2xl font-bold text-(--admin-green)">{totalHoras.toFixed(1)}</div>
          <div className="text-[10px] text-(--admin-text3) mt-1">Suma de horas registradas</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-(--admin-surface) border border-(--admin-border) rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="flex-1 min-w-60">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Búsqueda</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--admin-text3)" />
              <input
                type="text"
                placeholder="ID, empresa, asesor u objetivo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="admin-search-input w-full pl-9"
              />
            </div>
          </div>

          {/* Empresa */}
          <div className="w-56">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Empresa</label>
            <SearchableSelect
              value={filtros.empresaId || ''}
              onChange={(v) => setFiltros({ ...filtros, empresaId: v || undefined })}
              options={[
                { value: '', label: 'Todas las empresas' },
                ...empresas.map((emp) => ({ value: emp.nitId, label: `${emp.nombre} (${emp.nitId})` })),
              ]}
              placeholder="Todas las empresas"
              searchPlaceholder="Buscar empresa..."
            />
          </div>

          {/* Asesor */}
          <div className="w-48">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Asesor</label>
            <SearchableSelect
              value={filtros.asesorId?.toString() || ''}
              onChange={(v) => setFiltros({ ...filtros, asesorId: v ? parseInt(v, 10) : undefined })}
              options={[
                { value: '', label: 'Todos los asesores' },
                ...asesores.map((a) => ({ value: String(a.asesorId), label: a.nombre })),
              ]}
              placeholder="Todos los asesores"
              searchPlaceholder="Buscar asesor..."
            />
          </div>

          {/* Estado */}
          <div className="w-44">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Estado</label>
            <SearchableSelect
              value={filtros.estado || ''}
              onChange={(v) => setFiltros({ ...filtros, estado: (v || undefined) as AsesoriasSysadminFilter['estado'] })}
              options={[
                { value: '', label: 'Cualquier estado' },
                ...ESTADOS.map((estado) => ({ value: estado, label: getAsesoriaEstadoLabel(estado) })),
              ]}
              placeholder="Cualquier estado"
              searchPlaceholder="Buscar estado..."
            />
          </div>

          {/* Group toggle */}
          <div className="border-l border-(--admin-border) pl-4">
            <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Agrupar por</label>
            <div className="flex bg-(--admin-surface2) p-0.5 rounded-lg border border-(--admin-border)">
              <button
                type="button"
                onClick={() => setGroupBy('none')}
                className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'none' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
              >
                Nada
              </button>
              <button
                type="button"
                onClick={() => setGroupBy('empresa')}
                className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'empresa' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
              >
                Empresa
              </button>
            </div>
          </div>

          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="admin-btn text-[11px] py-1.5 px-3 bg-white border-(--admin-border) text-(--admin-text2)"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="admin-card">
        <div className="admin-card-header flex items-center justify-between gap-3">
          <div className="text-[13px] font-semibold text-(--admin-text) flex items-center gap-2">
            Asesorías
            <span className="bg-(--admin-surface2) px-1.5 py-0.5 rounded text-(--admin-text3) font-normal text-[11px] border border-(--admin-border)">
              {sorted.length}
            </span>
          </div>
          <ColumnPicker cols={ASESORIAS_COLS} visible={visible} onToggle={toggleCol} onReset={resetCols} activeCount={colCount} />
        </div>
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                {visibleCols.map((c) => (
                  <th key={c.key} style={COL_ALIGN[c.key] ? { textAlign: COL_ALIGN[c.key] } : undefined}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={visibleCols.length} className="text-center py-12 text-(--admin-text3)">
                    <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <div className="text-[14px] font-medium">No se encontraron asesorías</div>
                    <div className="text-[12px] opacity-70">Intenta ajustar los filtros de búsqueda</div>
                  </td>
                </tr>
              ) : groupBy === 'empresa' ? (
                groups?.map(([empresaId, group]) => (
                  <Fragment key={empresaId}>
                    <tr className="bg-(--admin-bg) border-y border-(--admin-border)">
                      <td colSpan={visibleCols.length} className="py-2.5 px-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-(--admin-text2) uppercase tracking-widest">{group.nombre}</span>
                            <span className="text-[10px] text-(--admin-text3) bg-white px-1.5 py-0.5 rounded border border-(--admin-border)">
                              {group.items.length}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-(--admin-text3) font-bold uppercase tracking-tighter">Total horas:</span>
                            <span className="text-[14px] font-bold text-(--admin-text)">{group.totalHoras.toFixed(1)}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {group.items.map((a) => (
                      <AsesoriaRow key={a.asesoriaId} asesoria={a} visible={visible} onEdit={setEditAsesoria} />
                    ))}
                  </Fragment>
                ))
              ) : (
                sorted.map((a) => <AsesoriaRow key={a.asesoriaId} asesoria={a} visible={visible} onEdit={setEditAsesoria} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      <EditarAsesoriaModal asesoria={editAsesoria} onClose={() => setEditAsesoria(null)} onSaved={refetch} />
    </div>
  );
}

// ─── Row ────────────────────────────────────────────────

function AsesoriaRow({
  asesoria,
  visible,
  onEdit,
}: {
  asesoria: AsesoriaSysadminDTO;
  visible: Record<AsesoriaColKey, boolean>;
  onEdit: (asesoria: AsesoriaSysadminDTO) => void;
}) {
  // Normalizar (no el crudo): una asesoría EN_REVISION ya marcada autoAprobar está resuelta y
  // debe verse como Finalizada, igual que en el resto de vistas (preaprobación transparente).
  const estado = normalizeAsesoriaEstado({ estado: asesoria.estado, autoAprobar: asesoria.autoAprobar });
  return (
    <tr className="clickable" onClick={() => onEdit(asesoria)}>
      {visible.asesoriaId && (
        <td className="text-(--admin-text2) font-mono" style={{ textAlign: 'right' }}>
          {asesoria.asesoriaId}
        </td>
      )}
      {visible.empresaNombre && <td className="text-(--admin-text) font-medium">{asesoria.empresaNombre || '—'}</td>}
      {visible.asesorNombre && <td className="text-(--admin-text2)">{asesoria.asesorNombre || '—'}</td>}
      {visible.estado && (
        <td style={{ textAlign: 'center' }}>
          <span className={`admin-chip ${estadoChipClass(estado)}`}>{getAsesoriaEstadoLabel(estado)}</span>
        </td>
      )}
      {visible.fechaInicio && <td className="text-(--admin-text2) whitespace-nowrap">{formatDate(asesoria.fechaInicio)}</td>}
      {visible.fechaFin && <td className="text-(--admin-text2) whitespace-nowrap">{formatDate(asesoria.fechaFin)}</td>}
      {visible.horas && (
        <td className="text-(--admin-text) font-medium" style={{ textAlign: 'right' }}>
          {asesoria.horas.toFixed(1)}
        </td>
      )}
      {visible.objetivo && (
        <td className="text-(--admin-text2) max-w-xs truncate" title={asesoria.objetivo}>
          {asesoria.objetivo || '—'}
        </td>
      )}
      {visible.orden && (
        <td className="text-(--admin-text2)" style={{ textAlign: 'right' }}>
          {asesoria.orden ?? '—'}
        </td>
      )}
      {visible.audiosCount && (
        <td className="text-(--admin-text2)" style={{ textAlign: 'right' }}>
          {asesoria.audiosCount}
        </td>
      )}
      {visible.evidenciasCount && (
        <td className="text-(--admin-text2)" style={{ textAlign: 'right' }}>
          {asesoria.evidenciasCount}
        </td>
      )}
      {visible.evaluacionesCount && (
        <td className="text-(--admin-text2)" style={{ textAlign: 'right' }}>
          {asesoria.evaluacionesCount}
        </td>
      )}
    </tr>
  );
}
